import React, { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { mergeClasses, makeStyles, tokens } from '@fluentui/react-components';
import { MessageBar, MessageBarBody } from '@fluentui/react-message-bar';
import {
  useWizardService,
  getCurrentStepId,
  getIsStepValid,
  getIsStepSaveFailed,
} from '@bingads-webui-react/wizard-core';

export const useStyles = makeStyles({
  messageBar: {
    whiteSpace: 'normal',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
});

interface ErrorBarProps {
  errorMessage?: string;
  classes?: {
    messageBar?: string;
  };
}

export const ErrorBar: React.FC<ErrorBarProps> = ({ errorMessage, classes: propClasses }) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useStyles();
  const { state } = useWizardService();
  const currentStepId = getCurrentStepId(state);
  const isValid = getIsStepValid(state, currentStepId);
  const isSaveFailed = getIsStepSaveFailed(state, currentStepId);
  const showError = !isValid && isSaveFailed;

  if (!showError) {
    return null;
  }

  return (
    <MessageBar intent="error" className={mergeClasses('error-message-bar', classes.messageBar, propClasses?.messageBar)}>
      <MessageBarBody>
        {errorMessage || i18n.getString(_TL_('We\'ve encountered an error saving your updates. Please review and try again.'))}
      </MessageBarBody>
    </MessageBar>
  );
};

export const withErrorAbove = <P extends object>(
  Component: React.ComponentType<P>,
): React.FC<P & ErrorBarProps> => (props: P & ErrorBarProps) => {
    const {
      errorMessage,
      classes,
      ...rest
    } = props;

    return (
      <>
        <ErrorBar errorMessage={errorMessage} classes={classes} />
        <Component {...(rest as P)} />
      </>
    );
  };
